import { ANALYTICS_TIERS, displayPlan } from "../../constants/customers";

/**
 * Results for the "Add Customer" search: every matching website, with the
 * customers already on the page included (flagged `listed`) and their current
 * tier preselected so the tier can be changed from the same control.
 */
export function buildAddCustomerResults(websites, customerByProdId, term, limit = 10) {
  const q = (term || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const matches = (Array.isArray(websites) ? websites : []).filter(
    (w) =>
      (w.website_name || "").toLowerCase().includes(q) ||
      (w.website_id || "").toLowerCase().includes(q)
  );
  const rows = matches.map((w) => {
    const listed = (customerByProdId && customerByProdId[w.website_id]) || null;
    const currentPlan = displayPlan(listed ? listed.plan : w.subscription_plan);
    return {
      website: w,
      listed: !!listed,
      currentPlan,
      // Listed customers are always on an analytics tier; keep the control valid otherwise.
      tierOptions: ANALYTICS_TIERS.includes(currentPlan) || !listed ? ANALYTICS_TIERS : [currentPlan, ...ANALYTICS_TIERS],
    };
  });
  // Already-tracked customers first (they are what the operator is most likely looking for).
  rows.sort((a, b) => Number(b.listed) - Number(a.listed));
  return rows.slice(0, limit);
}

/** Later of last login / last activity → { ts, label } (null when neither is known). */
export function mostRecentConsoleSignal(lastLoginAt, lastActivityAt) {
  const login = lastLoginAt instanceof Date && !Number.isNaN(lastLoginAt.getTime()) ? lastLoginAt : null;
  const activity = lastActivityAt instanceof Date && !Number.isNaN(lastActivityAt.getTime()) ? lastActivityAt : null;
  if (!login && !activity) return null;
  if (!activity || (login && login >= activity)) return { ts: login, label: "Login" };
  return { ts: activity, label: "Active" };
}

const fmtDay = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null);

/** Hover breakdown for the "Findings acted on" cell. `findings` is the normalized overview block or null. */
export function findingsBreakdown(findings) {
  if (!findings) {
    return { headline: "Not reported", rows: [{ label: "Platform on this environment does not expose finding interactions yet", value: "" }] };
  }
  const days = findings.days || 30;
  const rows = [
    { label: "Marked done", value: String(findings.done) },
    { label: "Discarded", value: String(findings.discarded) },
  ];
  Object.entries(findings.discard_reasons || {})
    .sort((a, b) => b[1] - a[1])
    .forEach(([reason, n]) => rows.push({ label: `· ${reason}`, value: String(n), sub: true }));
  rows.push({ label: "All-time", value: `${findings.done_total} done · ${findings.discarded_total} discarded` });
  rows.push({ label: "Open now", value: `${findings.open_total} of ${findings.findings_total}` });
  const last = fmtDay(findings.last_interaction_at);
  if (last) rows.push({ label: "Last interaction", value: last });
  return {
    headline: `${findings.interacted} acted on in the last ${days} days`,
    rows,
  };
}

/** Hover breakdown for the "Agents / Mo" cell: the distinct agents that ran in the window. */
export function agentsBreakdown(customer, hubBase) {
  const agents = Array.isArray(customer?.agents) ? customer.agents : [];
  const rows = agents.map((a) => ({
    label: a.title || a.slug || a.id || "Agent",
    value: `${a.runs} run${a.runs === 1 ? "" : "s"}${fmtDay(a.last_run_at) ? ` · last ${fmtDay(a.last_run_at)}` : ""}`,
    href: hubBase && a.slug ? `${hubBase}/agents/${a.slug}/launch` : null,
  }));
  if (!rows.length) rows.push({ label: "No agent runs in the last 30 days", value: "" });
  if (customer?.agentsAllTime > agents.length) {
    rows.push({ label: "All-time", value: `${customer.agentsAllTime} distinct agents`, sub: true });
  }
  return {
    headline: `${customer?.agents30d ?? 0} agent${customer?.agents30d === 1 ? "" : "s"} ran in the last 30 days · ${customer?.runs30d ?? 0} run${customer?.runs30d === 1 ? "" : "s"}`,
    rows,
  };
}
